import {
  IEntityStoryColorSettings,
  IEntityStorySettings,
  IEntityStorySettingsGraphqlResponse,
  ISettings
} from '../../types';

/* This function transforms the graphQL response for the getSettings query / updateSettings mutation
to the known ISettings type */
export const processEntitySettings = (response: IEntityStorySettingsGraphqlResponse): ISettings => {
  const entitySettings = response.entitySettings;
  const bodyBackground = response.bodyBackground;
  const bodyFont = response.bodyFont;
  const headerBackground = response.headerBackground;
  const headerFont = response.headerFont;
  const footerBackground = response.footerBackground;
  const footerFont = response.footerFont;

  return {
    entity_id: entitySettings.entityId,
    advanced: {
      google_analytics_id: entitySettings.googleAnalyticsId
    },
    brand: {
      fonts: {
        paragraph: entitySettings.fontsParagraph,
        headline: entitySettings.fontsHeadline
      },
      logo: {
        placement: {
          header_as_footer: entitySettings.logoPlacementHeaderAsFooter,
          header_only: entitySettings.logoPlacementHeaderOnly,
          unique_footer: entitySettings.logoPlacementUniqueFooter,
          footer_only: entitySettings.logoPlacementFooterOnly
        },
        header: entitySettings.logoHeader,
        footer: entitySettings.logoFooter
      },
      colors: {
        header: {
          background: {
            swatch: headerBackground.swatches,
            active: headerBackground.active
          },
          font: {
            swatch: headerFont.swatches,
            active: headerFont.active
          }
        },
        body: {
          background: {
            swatch: bodyBackground.swatches,
            active: bodyBackground.active
          },
          font: {
            swatch: bodyFont.swatches,
            active: bodyFont.active
          }
        },
        footer: {
          background: {
            swatch: footerBackground.swatches,
            active: footerBackground.active
          },
          font: {
            swatch: footerFont.swatches,
            active: footerFont.active
          }
        }
      }
    }
  } as ISettings;
};

// This function transforms ISettings to IEntityStorySettings type accepted by graphQL.
// entityId is intentionally excluded — it is passed separately as the pk_columns arg,
// not as part of the _set input (Hasura rejects PK fields in _set).
export const processPutSettings = (request: ISettings): Omit<IEntityStorySettings, 'entityId'> => {
  return {
    logoHeader: request.brand.logo.header,
    logoFooter: request.brand.logo.footer,
    logoPlacementHeaderAsFooter: request.brand.logo.placement.header_as_footer,
    logoPlacementHeaderOnly: request.brand.logo.placement.header_only,
    logoPlacementUniqueFooter: request.brand.logo.placement.unique_footer,
    logoPlacementFooterOnly: request.brand.logo.placement.footer_only,
    fontsHeadline: request.brand.fonts.headline,
    fontsParagraph: request.brand.fonts.paragraph,
    googleAnalyticsId: request.advanced.google_analytics_id
  };
};

// This function transforms ISettings to IEntityStoryColorSettings type accepted by graphQL
export const processPutColors = (request: ISettings): IEntityStoryColorSettings => {
  return {
    bodyBackground: {
      active: request.brand.colors.body.background.active,
      swatches: request.brand.colors.body.background.swatch
    },
    bodyFont: {
      active: request.brand.colors.body.font.active,
      swatches: request.brand.colors.body.font.swatch
    },
    headerBackground: {
      active: request.brand.colors.header.background.active,
      swatches: request.brand.colors.header.background.swatch
    },
    headerFont: {
      active: request.brand.colors.header.font.active,
      swatches: request.brand.colors.header.font.swatch
    },
    footerBackground: {
      active: request.brand.colors.footer.background.active,
      swatches: request.brand.colors.footer.background.swatch
    },
    footerFont: {
      active: request.brand.colors.footer.font.active,
      swatches: request.brand.colors.footer.font.swatch
    }
  } as IEntityStoryColorSettings;
};
